// Material catalogue used by the programmatic /rates/:materialSlug/:citySlug
// pages. `grade` must match `scrap_indices.material_grade` in the database so
// the live price row can be resolved for any material x city permutation.

export interface MaterialMeta {
  slug: string;
  /** DB material_grade value */
  grade: string;
  /** Human-friendly display name used in headings and metadata */
  name: string;
  family: "Ferrous Scrap" | "Finished Steel" | "Minerals & DRI" | "Plastic Scrap";
  unit: "MT" | "KG";
  /** GST rate applicable in India for this material class */
  gstPct: number;
  hsn: string;
  blurb: string;
  specs: string[];
  buyers: string[];
}

export const MATERIALS: MaterialMeta[] = [
  {
    slug: "hms-1",
    grade: "HMS 1",
    name: "HMS 1",
    family: "Ferrous Scrap",
    unit: "MT",
    gstPct: 18,
    hsn: "7204",
    blurb:
      "Heavy Melting Scrap Grade 1 is clean, oversized ferrous scrap with a minimum 6mm thickness and no cast iron, coated or sealed pieces. It is the primary melting charge for induction furnaces and EAF mills.",
    specs: [
      "Minimum thickness 6mm, pieces sized for direct furnace charging",
      "Fe content typically 92% and above",
      "Free of cast iron, sealed containers, oil, rubber and non-ferrous attachments",
    ],
    buyers: ["Induction furnace units", "EAF steel mills", "Ingot and billet makers"],
  },
  {
    slug: "hms-2",
    grade: "HMS 2",
    name: "HMS 2",
    family: "Ferrous Scrap",
    unit: "MT",
    gstPct: 18,
    hsn: "7204",
    blurb:
      "HMS 2 is mixed-thickness ferrous melting scrap, typically 3mm and above, including galvanised and blackened sheet. It trades at a discount to HMS 1 and is blended into the furnace charge to manage cost.",
    specs: [
      "Mixed thickness from 3mm upward, including sheet and pipe cuttings",
      "Fe content typically 88–92%",
      "Galvanised material permitted, no sealed or oil-filled pieces",
    ],
    buyers: ["Induction furnace units", "Re-rolling mills", "Foundries"],
  },
  {
    slug: "cast-iron",
    grade: "Cast Iron Scrap",
    name: "Cast Iron Scrap",
    family: "Ferrous Scrap",
    unit: "MT",
    gstPct: 18,
    hsn: "7204",
    blurb:
      "Cast iron scrap covers engine blocks, machine beds, pipes and CI borings recycled into grey and ductile iron castings. High carbon content makes it a cost-efficient charge for cupola and induction foundries.",
    specs: [
      "Grey / ductile iron machine and automotive castings",
      "High carbon, low tramp element content",
      "Free of steel attachments, bearings and non-ferrous inserts",
    ],
    buyers: ["Grey iron foundries", "Ductile iron pipe makers", "Auto component casters"],
  },
  {
    slug: "turning-boring",
    grade: "Turning & Boring",
    name: "Turning & Boring Scrap",
    family: "Ferrous Scrap",
    unit: "MT",
    gstPct: 18,
    hsn: "7204",
    blurb:
      "Machining turnings and borings generated by lathe and CNC shops. Bulk density and oil content drive the price discount, so briquetted or de-oiled lots command a premium.",
    specs: [
      "Loose or briquetted steel turnings, low free oil preferred",
      "Bulk density 0.7–1.2 t/m3 depending on briquetting",
      "Free of grinding dust, coolant sludge and non-ferrous swarf",
    ],
    buyers: ["Induction furnaces", "Briquetting units", "Ingot makers"],
  },
  {
    slug: "shredded-scrap",
    grade: "Shredded Scrap",
    name: "Shredded Scrap",
    family: "Ferrous Scrap",
    unit: "MT",
    gstPct: 18,
    hsn: "7204",
    blurb:
      "Shredded scrap is fragmentised, magnetically separated ferrous scrap with consistent size and chemistry — the most predictable melting charge available and a direct substitute for imported shredded.",
    specs: [
      "Fragmentised to 50–200mm, magnetically separated",
      "Fe content 92%+ with low residuals",
      "Consistent bulk density for continuous charging",
    ],
    buyers: ["EAF mills", "Automotive-grade steel makers", "Large induction plants"],
  },
  {
    slug: "ms-billets",
    grade: "MS Billet",
    name: "MS Billets",
    family: "Finished Steel",
    unit: "MT",
    gstPct: 18,
    hsn: "7207",
    blurb:
      "Mild steel billets are semi-finished squares (100mm–160mm) rolled into TMT bars, wire rod and structurals. Billet rates set the floor for rebar pricing across regional markets.",
    specs: [
      "100x100mm to 160x160mm sections, 3–6m lengths",
      "IS 2830 chemistry, carbon 0.14–0.25%",
      "Test certificate with heat number for every lot",
    ],
    buyers: ["Re-rolling mills", "TMT bar producers", "Wire rod plants"],
  },
  {
    slug: "tmt-bar",
    grade: "TMT Bar",
    name: "TMT Bars",
    family: "Finished Steel",
    unit: "MT",
    gstPct: 18,
    hsn: "7214",
    blurb:
      "Thermo-mechanically treated reinforcement bars in Fe 500 and Fe 550D grades, supplied in 8mm–32mm diameters for infrastructure and real-estate projects.",
    specs: [
      "IS 1786 Fe 500 / Fe 500D / Fe 550D grades",
      "8mm to 32mm diameter, 12m standard length",
      "Mill test certificate and rib-pattern branding",
    ],
    buyers: ["Infrastructure contractors", "Real-estate developers", "Steel distributors"],
  },
  {
    slug: "ms-ingot",
    grade: "MS Ingot",
    name: "MS Ingots",
    family: "Finished Steel",
    unit: "MT",
    gstPct: 18,
    hsn: "7206",
    blurb:
      "Mild steel ingots cast by induction furnace units and supplied to re-rollers producing angles, channels, flats and light structurals.",
    specs: [
      "Cast weight 40–120 kg per ingot",
      "Carbon 0.15–0.25%, sulphur and phosphorus below 0.05%",
      "Sound surface, free of pipe and cold shuts",
    ],
    buyers: ["Re-rolling mills", "Structural steel makers", "Traders"],
  },
  {
    slug: "pig-iron",
    grade: "Pig Iron",
    name: "Pig Iron",
    family: "Finished Steel",
    unit: "MT",
    gstPct: 18,
    hsn: "7201",
    blurb:
      "Basic and foundry-grade pig iron used to dilute residuals in the furnace charge and stabilise chemistry where high-quality scrap is scarce.",
    specs: [
      "Foundry grade: Si 1.5–3.5%, basic grade: Si below 1%",
      "Carbon 3.5–4.5%, low sulphur",
      "Supplied in cast bars with heat-wise analysis",
    ],
    buyers: ["Ductile iron foundries", "EAF mills", "Auto casting units"],
  },
  {
    slug: "sponge-iron",
    grade: "Sponge Iron",
    name: "Sponge Iron (DRI)",
    family: "Minerals & DRI",
    unit: "MT",
    gstPct: 18,
    hsn: "7203",
    blurb:
      "Direct reduced iron produced in coal-based rotary kilns. Metallisation and FeT determine yield, and sponge iron is the dominant furnace charge for India's secondary steel sector.",
    specs: [
      "Metallisation 80% minimum, FeT 88–92%",
      "Lump / fines split declared per lot",
      "Low sulphur, moisture below 1%",
    ],
    buyers: ["Induction furnaces", "Billet and ingot makers", "EAF mills"],
  },
  {
    slug: "iron-pellets",
    grade: "Iron Pellets",
    name: "Iron Ore Pellets",
    family: "Minerals & DRI",
    unit: "MT",
    gstPct: 18,
    hsn: "2601",
    blurb:
      "Fired iron ore pellets with 63–65% Fe used as DRI kiln and blast furnace feed. Pricing tracks iron ore fines and pellet premium movements.",
    specs: [
      "Fe 63–65%, size 6–18mm",
      "Tumbler index above 92%, low fines",
      "Basicity and swelling index reported per rake",
    ],
    buyers: ["Sponge iron plants", "Blast furnace operators", "Pellet traders"],
  },
  {
    slug: "dolomite",
    grade: "Dolomite",
    name: "Dolomite",
    family: "Minerals & DRI",
    unit: "MT",
    gstPct: 5,
    hsn: "2518",
    blurb:
      "Calcined and raw dolomite used as furnace flux and refractory lining feed in steel plants, sponge iron kilns and ferro-alloy units.",
    specs: [
      "MgO 18–21%, CaO 28–31%",
      "Sized 5–20mm, 20–40mm or lumps as required",
      "Low silica and moisture, chemical analysis per dispatch",
    ],
    buyers: ["Steel plants", "Sponge iron kilns", "Refractory makers"],
  },
];

export const getMaterialBySlug = (slug: string) =>
  MATERIALS.find((m) => m.slug === slug.toLowerCase());

export const getRelatedMaterials = (slug: string, count = 4) => {
  const current = getMaterialBySlug(slug);
  if (!current) return MATERIALS.slice(0, count);
  const same = MATERIALS.filter((m) => m.slug !== slug && m.family === current.family);
  const rest = MATERIALS.filter((m) => m.slug !== slug && m.family !== current.family);
  return [...same, ...rest].slice(0, count);
};

/** Maps a material to the marketplace scrap_category filter, when one exists. */
export const MATERIAL_TO_CATEGORY: Record<string, string> = {
  "hms-1": "HMS 1",
  "hms-2": "HMS 2",
  "cast-iron": "Cast Iron",
  "turning-boring": "Turnings",
  "shredded-scrap": "Shredded",
  "ms-billets": "Billets",
  dolomite: "Dolomite",
};
